import sumBy from 'lodash/sumBy'
import moment from 'moment'
import React from 'react'
import {
  CartesianGrid,
  ComposedChart,
  Label,
  Legend,
  Line,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import LegendComponent from '../../../common/legend/Legend'
import { i18n } from '../../index'
import { DailySalesNote } from '../customerSales/CustomerSalesTable'
import css from '../incentiveSales/incentiveSales.css'
import ChartLegend from './ChartLegend'
import { calcWastagePercent } from './WastageTable'
interface Props {
  month: number
  year: number
  data?: {
    reportMonthlyWastage: DailySalesNote[]
    getWastageBoundariesForProductType: {
      id: string
      productType: string
      lowerWastageBounds: number
      upperWastageBounds: number
    }
  }
}
const STROKES = {
  UPPERBOUNDS: '#78A22F',
  WASTAGE: '#f06767',
  LOWERBOUNDS: '#F7AC01',
}

type State = {}

class WastageGraph extends React.Component<Props, State> {
  renderWeekAreas = () => {
    const { data } = this.props ?? {}
    const { reportMonthlyWastage = [] } = data ?? {}
    let firstDayOfWeek, lastDayOfWeek, week
    const areas: JSX.Element[] = []
    reportMonthlyWastage.forEach((obj, idx) => {
      const date = obj.date
      const weekOfDate = moment(date).week()

      if (!week) {
        week = weekOfDate
        firstDayOfWeek = date
      } else {
        if (weekOfDate === week) {
          lastDayOfWeek = date
        } else {
          areas.push(
            <ReferenceArea
              key={firstDayOfWeek}
              x1={firstDayOfWeek}
              x2={lastDayOfWeek}
              ifOverflow='extendDomain'
              shape={OffsetShape}
            />,
          )
          week = weekOfDate
          firstDayOfWeek = date
          lastDayOfWeek = undefined
        }
      }

      if (idx === reportMonthlyWastage.length - 1) {
        areas.push(
          <ReferenceArea
            key={firstDayOfWeek}
            x1={firstDayOfWeek}
            x2={lastDayOfWeek}
            ifOverflow='extendDomain'
            shape={OffsetShape}
          />,
        )
      }
    })
    return areas
  }

  render() {
    const { month, year, data } = this.props
    const {
      reportMonthlyWastage = [],
      getWastageBoundariesForProductType = { lowerWastageBounds: 0, upperWastageBounds: 0 },
    } = data ?? {}
    const displayedMonth = moment()
      .year(year)
      .month(month - 1)
      .format('YYYY-MM')
    const date = moment()
    let legendComponent = <LegendComponent />
    const lowerWastageBounds = getWastageBoundariesForProductType?.lowerWastageBounds
    const upperWastageBounds = getWastageBoundariesForProductType?.upperWastageBounds
    const graphData = reportMonthlyWastage.map((obj) => {
      const salesLossPercentage = calcWastagePercent(obj.salesAmount, obj.wastageAmount)
      return {
        ...obj,
        salesLossPercentage,
        lowerWastageBounds,
        upperWastageBounds,
      }
    })

    if (displayedMonth === date.format('YYYY-MM')) {
      const cumulativeData = reportMonthlyWastage.slice(0, date.date())
      const combinedSalesAmount = sumBy(cumulativeData, 'salesAmount')
      const combinedWastageAmount = sumBy(cumulativeData, 'wastageAmount')
      const cumulativeWastagePercentage = calcWastagePercent(combinedSalesAmount, combinedWastageAmount).toFixed(2)
      const processedData = {
        Hävikki: `${cumulativeWastagePercentage}%`,
        Alaraja: `${lowerWastageBounds}%`,
        Yliraja: `${upperWastageBounds}%`,
      }
      legendComponent = <ChartLegend additionalValues={processedData} />
    }

    return (
      <div>
        <h4 className={css.month}>
          {moment()
            .year(year)
            .month(month - 1)
            .format('MMMM YYYY')}
        </h4>
        <div className={css.chart}>
          <div>
            <ResponsiveContainer width='100%' height={300}>
              <ComposedChart
                margin={{
                  top: 30,
                  right: 10,
                  bottom: 10,
                  left: 10,
                }}
                data={graphData}
              >
                <CartesianGrid />

                <XAxis
                  dataKey={'date'}
                  interval={0}
                  tickFormatter={(item) => moment(item).format('D')}
                  axisLine={{
                    stroke: '#ccc',
                    strokeWidth: 1,
                  }}
                  padding={{ left: 17, right: 17 }}
                  tickSize={0}
                  tickMargin={10}
                  tick={{
                    fontSize: 12,
                  }}
                />
                <YAxis
                  padding={{
                    top: 0,
                  }}
                  axisLine={{
                    stroke: '#ccc',
                    strokeWidth: 1,
                  }}
                  tickCount={6}
                  tickSize={0}
                  tickMargin={10}
                  tick={{
                    fontSize: 12,
                  }}
                >
                  <Label position='top' offset={15} />
                </YAxis>

                <Tooltip />

                <Legend content={legendComponent} />

                <Line
                  type='linear'
                  dataKey={'salesLossPercentage'}
                  dot={false}
                  name={i18n.t('reporting.wastage')}
                  legendType='line'
                  strokeWidth={3}
                  stroke={STROKES.WASTAGE}
                />

                <Line
                  type='linear'
                  dataKey={'lowerWastageBounds'}
                  dot={false}
                  name={i18n.t('reporting.lowerBounds')}
                  legendType='line'
                  strokeWidth={3}
                  stroke={STROKES.LOWERBOUNDS}
                />

                <Line
                  type='linear'
                  dataKey={'upperWastageBounds'}
                  dot={false}
                  name={i18n.t('reporting.upperBounds')}
                  legendType='line'
                  strokeWidth={3}
                  stroke={STROKES.UPPERBOUNDS}
                />
                {this.renderWeekAreas()}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    )
  }
} // Default ReferenceAreas would not line up with the charts so needed to make a custom shape with x offset of 17

const OffsetShape = (props) => {
  const x = props.x
  const y = props.y
  const w = props.width
  const h = props.height
  const d = `M ${x},${y} h ${w} v ${h} h ${-w} Z`
  return (
    <g>
      <path
        r={props.r}
        fill='#ccc'
        fillOpacity='0.2'
        stroke='none'
        strokeWidth='1'
        radius='0'
        className='recharts-rectangle recharts-reference-area-rect'
        d={d}
      />
    </g>
  )
}

export default WastageGraph
